import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  Col,
  Form,
  Modal,
  Nav,
  Row,
  Spinner,
  Tab,
  Table,
} from "react-bootstrap";
import DataTable from "react-data-table-component";
import { AvForm, AvField } from "availity-reactstrap-validation";
import moment from "moment";
import { toast } from "react-toastify";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import Header from "../../layouts/Header";
import DiscountSurfaces from "../../components/admin/DiscountSurfaces";
import Footer from "../../layouts/Footer";
import DataTableSkeleton from "../../components/DataTableSkeleton";
import AdminEmptyState from "../../components/admin/AdminEmptyState";
import AdminAddButton from "../../components/admin/AdminAddButton";
import ConfirmModal from "../../components/ConfirmModal";
import DashboardStatCard from "../../components/dashboard/DashboardStatCard";
import { getAffiliateUsers } from "../../helper/affiliate_helper";
import {
  createReferralDiscount,
  deactivateReferralDiscount,
  formatCurrency,
  formatUsageDisplay,
  getReferralDiscountAnalytics,
  getReferralDiscountDetails,
  getReferralDiscounts,
  getReferralDiscountStatusBadge,
  normalizeReferralDiscountAnalytics,
  normalizeReferralDiscountDetail,
  normalizeReferralDiscountList,
  normalizeReferralDiscountPagination,
  updateReferralDiscount,
} from "../../helper/referral_discount_helper";
import { dataTableCustomStyles, dataTablePaginationOptions } from "../../components/admin/dataTableConfig";
import * as Utils from "../../Utils";
import AdminRowActions, { AdminActionButton } from '../../components/admin/AdminRowActions';

const safeText = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  return String(value);
};

const getCodeId = (row) => row?.id || row?._id || null;

const getAffiliateIdFromRow = (row) =>
  row?.affiliateId?._id || row?.affiliateId?.id || row?.affiliateId || null;

const buildCreatePayload = (values) => {
  const affiliateId = String(values.affiliateId || "").trim();
  const referralCode = String(values.referralCode || "").trim().toUpperCase();
  const discountPercentage = Number(values.discountPercentage);

  const payload = {
    affiliateId,
    referralCode,
    discountPercentage,
    status: values.status || "active",
  };

  if (values.maxUsage !== "" && values.maxUsage !== null && values.maxUsage !== undefined) {
    payload.maxUsage = Number(values.maxUsage);
  } else {
    payload.maxUsage = null;
  }

  if (values.expiryDate) {
    payload.expiryDate = new Date(values.expiryDate).toISOString();
  } else {
    payload.expiryDate = null;
  }

  return payload;
};

const buildUpdatePayload = (values) => {
  const payload = {
    affiliateId: String(values.affiliateId || "").trim(),
    discountPercentage: Number(values.discountPercentage),
    status: values.status || "active",
  };

  if (values.maxUsage !== "" && values.maxUsage !== null && values.maxUsage !== undefined) {
    payload.maxUsage = Number(values.maxUsage);
  } else {
    payload.maxUsage = null;
  }

  if (values.expiryDate) {
    payload.expiryDate = new Date(values.expiryDate).toISOString();
  } else {
    payload.expiryDate = null;
  }

  return payload;
};

function ReferralDiscountDetails({ code, loading, affiliateName }) {
  if (loading) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" size="sm" />
        <p className="text-muted small mt-2 mb-0">Loading details…</p>
      </div>
    );
  }

  if (!code) return <p className="text-muted mb-0">No details available.</p>;

  const currency = "INR";

  return (
    <Table bordered responsive className="mb-0">
      <tbody>
        <tr>
          <th style={{ width: "200px" }}>Referral code</th>
          <td><strong>{safeText(code.referralCode)}</strong></td>
        </tr>
        <tr>
          <th>Affiliate</th>
          <td>{safeText(affiliateName || getAffiliateIdFromRow(code))}</td>
        </tr>
        <tr>
          <th>Discount</th>
          <td>{code.discountPercentage != null ? `${code.discountPercentage}%` : "—"}</td>
        </tr>
        <tr>
          <th>Usage</th>
          <td>{formatUsageDisplay(code.currentUsage, code.maxUsage)}</td>
        </tr>
        <tr>
          <th>Status</th>
          <td>
            <Badge bg={getReferralDiscountStatusBadge(code.status)}>
              {code.status === "active" ? "Active" : "Inactive"}
            </Badge>
          </td>
        </tr>
        <tr>
          <th>Expiry</th>
          <td>
            {code.expiryDate ? moment(code.expiryDate).format("DD MMM YYYY") : "No expiry"}
          </td>
        </tr>
        <tr>
          <th>Created</th>
          <td>
            {code.createdAt ? moment(code.createdAt).format("DD MMM YYYY, hh:mm A") : "—"}
          </td>
        </tr>
        <tr>
          <th>Updated</th>
          <td>
            {code.updatedAt ? moment(code.updatedAt).format("DD MMM YYYY, hh:mm A") : "—"}
          </td>
        </tr>
        <tr>
          <th>ID</th>
          <td><code className="small">{safeText(getCodeId(code))}</code></td>
        </tr>
        <tr>
          <th>Affiliate ID</th>
          <td><code className="small">{safeText(getAffiliateIdFromRow(code))}</code></td>
        </tr>
        <tr>
          <th>Revenue note</th>
          <td className="small text-muted">Commission is calculated on discounted subtotal ({currency}).</td>
        </tr>
      </tbody>
    </Table>
  );
}

function ReferralDiscountAnalyticsPanel({ analytics, loading, error, onRefresh }) {
  const currency = analytics?.summary?.currency || "INR";
  const summary = analytics?.summary || {};

  const monthlyChartData = useMemo(
    () =>
      (analytics?.monthlyReports || []).map((row) => ({
        label: `${row.month}/${row.year}`,
        usages: row.usages || 0,
        revenue: row.revenue || 0,
      })),
    [analytics?.monthlyReports]
  );

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
        <p className="text-muted small mt-2 mb-0">Loading analytics…</p>
      </div>
    );
  }

  if (error) {
    return (
      <AdminEmptyState
        icon="ri-bar-chart-line"
        title="Analytics unavailable"
        description={error}
        actionLabel="Retry"
        onAction={onRefresh}
      />
    );
  }

  return (
    <div>
      <Row className="g-3 mb-4">
        <Col md={4}>
          <DashboardStatCard
            label="Total usages"
            value={summary.totalReferrals ?? 0}
            icon="ri-coupon-3-line"
            iconBg="#DBEAFE"
            iconColor="#2563EB"
            accentColor="#2563EB"
          />
        </Col>
        <Col md={4}>
          <DashboardStatCard
            label="Total discount given"
            value={formatCurrency(summary.totalDiscountGiven, currency)}
            icon="ri-percent-line"
            iconBg="#FEF3C7"
            iconColor="#D97706"
            accentColor="#D97706"
          />
        </Col>
        <Col md={4}>
          <DashboardStatCard
            label="Total revenue"
            value={formatCurrency(summary.totalRevenue, currency)}
            icon="ri-money-rupee-circle-line"
            iconBg="#DCFCE7"
            iconColor="#16A34A"
            accentColor="#16A34A"
          />
        </Col>
      </Row>

      <Row className="g-3">
        <Col lg={6}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Header className="bg-white border-bottom">
              <h6 className="mb-0">Top affiliates</h6>
            </Card.Header>
            <Card.Body className="p-0">
              <Table responsive hover className="mb-0">
                <thead>
                  <tr>
                    <th>Affiliate</th>
                    <th>Code</th>
                    <th>Usages</th>
                    <th>Discount</th>
                    <th>Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {(analytics?.topAffiliates || []).length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center text-muted small py-4">
                        No referral usage yet
                      </td>
                    </tr>
                  ) : (
                    analytics.topAffiliates.map((row) => (
                      <tr key={`${row.affiliateId}-${row.referralCode}`}>
                        <td>{safeText(row.name)}</td>
                        <td><strong>{safeText(row.referralCode)}</strong></td>
                        <td>{row.usageCount ?? 0}</td>
                        <td>{formatCurrency(row.discountGiven, currency)}</td>
                        <td>{formatCurrency(row.revenue, currency)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={6}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Header className="bg-white border-bottom">
              <h6 className="mb-0">Discount % distribution</h6>
            </Card.Header>
            <Card.Body className="p-0">
              <Table responsive hover className="mb-0">
                <thead>
                  <tr>
                    <th>Discount %</th>
                    <th>Codes</th>
                    <th>Discount given</th>
                  </tr>
                </thead>
                <tbody>
                  {(analytics?.discountDistribution || []).length === 0 ? (
                    <tr>
                      <td colSpan={3} className="text-center text-muted small py-4">
                        No distribution data
                      </td>
                    </tr>
                  ) : (
                    analytics.discountDistribution.map((row) => (
                      <tr key={row.discountPercentage}>
                        <td>{row.discountPercentage}%</td>
                        <td>{row.count ?? 0}</td>
                        <td>{formatCurrency(row.discountGiven, currency)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="border-0 shadow-sm mt-3">
        <Card.Header className="bg-white border-bottom d-flex justify-content-between align-items-center">
          <h6 className="mb-0">Monthly trend (last 12 months)</h6>
        </Card.Header>
        <Card.Body>
          {monthlyChartData.length === 0 ? (
            <p className="text-muted small mb-0 text-center py-4">No monthly data yet</p>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={monthlyChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="usages" name="Usages" fill="#2563EB" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card.Body>
      </Card>
    </div>
  );
}

export default function ReferralDiscounts() {
  const [activeTab, setActiveTab] = useState("codes");

  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");
  const [affiliateFilter, setAffiliateFilter] = useState("");

  const [showFormModal, setShowFormModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editCode, setEditCode] = useState(null);

  const [viewCode, setViewCode] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);

  const [confirm, setConfirm] = useState(false);
  const [currentData, setCurrentData] = useState(null);
  const [affiliateOptions, setAffiliateOptions] = useState([]);
  const [affiliatesLoading, setAffiliatesLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState(null);

  const affiliateNameMap = useMemo(() => {
    const map = {};
    affiliateOptions.forEach((a) => {
      const id = a._id || a.id;
      if (id) {
        map[String(id)] = a.name || a.email || String(id);
      }
    });
    return map;
  }, [affiliateOptions]);

  const resolveAffiliateName = useCallback(
    (row) => {
      const id = getAffiliateIdFromRow(row);
      if (!id) return "—";
      return affiliateNameMap[String(id)] || String(id);
    },
    [affiliateNameMap]
  );

  const fetchCodes = async () => {
    setLoading(true);
    try {
      const params = { page, limit: perPage };
      if (search.trim()) params.search = search.trim();
      if (statusFilter) params.status = statusFilter;
      if (affiliateFilter) params.affiliateId = affiliateFilter;

      const res = await getReferralDiscounts(params);
      const list = normalizeReferralDiscountList(res);
      const pagination = normalizeReferralDiscountPagination(res);

      if (res?.success === false && !list.length) {
        toast.error(res?.message || "Failed to load referral discount codes");
        setCodes([]);
        setTotalRows(0);
        return;
      }

      setCodes(list);
      setTotalRows(pagination?.total || list.length);
    } catch (error) {
      console.error("Failed to fetch referral discount codes:", error);
      const message = error?.message || "Failed to load referral discount codes";
      if (error?.status === 404) {
        toast.error("This discounts list returned 404. The route is not on the API server this admin app is using.");
      } else {
        toast.error(message);
      }
      setCodes([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchAffiliateOptions = async () => {
    setAffiliatesLoading(true);
    try {
      const res = await getAffiliateUsers();
      const list =
        res?.data?.affiliates ??
        res?.affiliates ??
        (Array.isArray(res?.data) ? res.data : []);
      setAffiliateOptions(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error("Failed to load affiliates:", error);
      setAffiliateOptions([]);
    } finally {
      setAffiliatesLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    setAnalyticsLoading(true);
    setAnalyticsError(null);
    try {
      const res = await getReferralDiscountAnalytics();
      if (res?.success === false) {
        throw new Error(res?.message || "Failed to load analytics");
      }
      setAnalytics(normalizeReferralDiscountAnalytics(res));
    } catch (error) {
      console.error("Failed to fetch referral discount analytics:", error);
      const message =
        error?.status === 404
          ? "Referral analytics returned 404 on this API server."
          : error?.message || "Failed to load analytics";
      setAnalyticsError(message);
      setAnalytics(normalizeReferralDiscountAnalytics(null));
    } finally {
      setAnalyticsLoading(false);
    }
  };

  useEffect(() => {
    fetchAffiliateOptions();
  }, []);

  useEffect(() => {
    if (activeTab === "codes") {
      fetchCodes();
    }
  }, [page, statusFilter, affiliateFilter, activeTab]);

  useEffect(() => {
    if (activeTab === "analytics") {
      fetchAnalytics();
    }
  }, [activeTab]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    fetchCodes();
  };

  const handleCloseFormModal = () => {
    setShowFormModal(false);
    setIsEdit(false);
    setEditCode(null);
  };

  const handleShowFormModal = (code = null) => {
    if (code) {
      setEditCode(code);
      setIsEdit(true);
    } else {
      setEditCode(null);
      setIsEdit(false);
    }
    setShowFormModal(true);
  };

  const handleViewDetails = async (code) => {
    const id = getCodeId(code);
    if (!id) {
      toast.error("Code ID not found");
      return;
    }

    setViewCode(code);
    setViewLoading(true);
    try {
      const res = await getReferralDiscountDetails(id);
      const detail = normalizeReferralDiscountDetail(res);
      if (detail && typeof detail === "object") {
        setViewCode(detail);
      }
    } catch (error) {
      console.error("Failed to load code details:", error);
      toast.error(error?.message || "Failed to load code details");
    } finally {
      setViewLoading(false);
    }
  };

  const handleValidSubmit = async (e, values) => {
    setSubmitting(true);
    try {
      let res;

      if (isEdit) {
        const id = getCodeId(editCode);
        if (!id) throw new Error("Code ID not found");

        const discountPercentage = Number(values.discountPercentage);
        if (Number.isNaN(discountPercentage) || discountPercentage < 1 || discountPercentage > 100) {
          toast.error("Discount must be between 1 and 100");
          setSubmitting(false);
          return;
        }

        if (!values.affiliateId) {
          toast.error("Select an affiliate");
          setSubmitting(false);
          return;
        }

        res = await updateReferralDiscount(id, buildUpdatePayload(values));
      } else {
        const discountPercentage = Number(values.discountPercentage);
        if (!values.affiliateId) {
          toast.error("Select an affiliate");
          setSubmitting(false);
          return;
        }
        if (!String(values.referralCode || "").trim()) {
          toast.error("Enter a referral code");
          setSubmitting(false);
          return;
        }
        if (Number.isNaN(discountPercentage) || discountPercentage < 1 || discountPercentage > 100) {
          toast.error("Discount must be between 1 and 100");
          setSubmitting(false);
          return;
        }

        res = await createReferralDiscount(buildCreatePayload(values));
      }

      if (res?.success === false) {
        toast.error(res?.message || "Failed to save referral discount code");
        return;
      }

      toast.success(
        res?.message || (isEdit ? "Referral discount code updated" : "Referral discount code created")
      );
      handleCloseFormModal();
      fetchCodes();
    } catch (error) {
      console.error("Error saving referral discount code:", error);
      if (error?.status === 409) {
        toast.error(error?.message || "This referral code already exists");
      } else if (error?.status === 404) {
        toast.error(error?.message || "Affiliate not found");
      } else {
        toast.error(error?.message || "Failed to save referral discount code");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeactivateClick = (code) => {
    setCurrentData({
      _id: getCodeId(code),
      actionType: "Delete",
      referralCode: code.referralCode,
      message: `This will deactivate code "${code.referralCode}". It cannot be used at checkout.`,
    });
    setConfirm(true);
  };

  const handleConfirm = async () => {
    if (!currentData?._id) return;

    setActionLoading(true);
    try {
      const res = await deactivateReferralDiscount(currentData._id);

      if (res?.success === false) {
        toast.error(res?.message || "Failed to deactivate code");
        return;
      }

      toast.success(res?.message || "Referral discount code deactivated");
      setConfirm(false);
      setCurrentData(null);
      fetchCodes();
    } catch (error) {
      console.error("Deactivate failed:", error);
      toast.error(error?.message || "Failed to deactivate code");
    } finally {
      setActionLoading(false);
    }
  };

  const columns = [
    {
      name: "Code",
      selector: (row) => row.referralCode || "",
      sortable: true,
      minWidth: "120px",
      cell: (row) => <strong>{safeText(row.referralCode)}</strong>,
    },
    {
      name: "Affiliate",
      selector: (row) => resolveAffiliateName(row),
      sortable: true,
      minWidth: "160px",
      cell: (row) => <span className="small">{resolveAffiliateName(row)}</span>,
    },
    {
      name: "Discount",
      selector: (row) => row.discountPercentage ?? 0,
      sortable: true,
      minWidth: "100px",
      center: true,
      cell: (row) => (row.discountPercentage != null ? `${row.discountPercentage}%` : "—"),
    },
    {
      name: "Status",
      minWidth: "110px",
      cell: (row) => (
        <Badge bg={getReferralDiscountStatusBadge(row.status)}>
          {row.status === "active" ? "Active" : "Inactive"}
        </Badge>
      ),
    },
    {
      name: "Usage",
      selector: (row) => row.currentUsage ?? 0,
      sortable: true,
      minWidth: "110px",
      center: true,
      cell: (row) => formatUsageDisplay(row.currentUsage, row.maxUsage),
    },
    {
      name: "Expiry",
      selector: (row) => (row.expiryDate ? new Date(row.expiryDate).getTime() : 0),
      sortable: true,
      minWidth: "120px",
      cell: (row) =>
        row.expiryDate ? moment(row.expiryDate).format("DD MMM YYYY") : "—",
    },
    {
      name: "Created",
      selector: (row) => (row.createdAt ? new Date(row.createdAt).getTime() : 0),
      sortable: true,
      minWidth: "120px",
      cell: (row) =>
        row.createdAt ? moment(row.createdAt).format("DD MMM YYYY") : "—",
    },
    {
      name: "Actions",
      minWidth: "150px",
      center: true,
      cell: (row) => (
        <AdminRowActions>
          <AdminActionButton action="view" label="View details" onClick={() => handleViewDetails(row)} />
          <AdminActionButton action="edit" onClick={() => handleShowFormModal(row)} />
          {row.status === "active" && (
            <AdminActionButton
              icon="ri-close-circle-line"
              label="Deactivate"
              tone="danger"
              onClick={() => handleDeactivateClick(row)}
            />
          )}
        </AdminRowActions>
      ),
      ignoreRowClick: true,
    },
  ];

  const editExpiryValue = editCode?.expiryDate
    ? moment(editCode.expiryDate).format("YYYY-MM-DD")
    : "";

  return (
    <React.Fragment>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <DiscountSurfaces />
        <div className="d-md-flex align-items-center justify-content-between mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="/admin/dashboard">Dashboard</Link></li>
              <li className="breadcrumb-item active" aria-current="page">Referral Discount Codes</li>
            </ol>
            <h4 className="main-title mb-0 admin-page-title">Referral Discount Codes</h4>
            <p className="text-secondary small mb-0 mt-1">
              Admin-managed discount codes tied to affiliates for checkout
            </p>
          </div>
          <div className="d-flex gap-2 mt-3 mt-md-0">
            <Button
              variant="outline-secondary"
              onClick={() => (activeTab === "codes" ? fetchCodes() : fetchAnalytics())}
              disabled={loading || analyticsLoading}
            >
              <i className={`ri-refresh-line me-1 ${loading || analyticsLoading ? "spin" : ""}`} />
              Refresh
            </Button>
            {activeTab === "codes" && (
              <AdminAddButton label="Add Code" onClick={() => handleShowFormModal()} />
            )}
          </div>
        </div>

        <Tab.Container activeKey={activeTab} onSelect={(key) => key && setActiveTab(key)}>
          <Nav variant="tabs" className="mb-3">
            <Nav.Item>
              <Nav.Link eventKey="codes">Codes</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="analytics">Analytics</Nav.Link>
            </Nav.Item>
          </Nav>

          <Tab.Content>
            <Tab.Pane eventKey="codes">
              <Card className="border-0 shadow-sm">
                <Card.Body>
                  <Form onSubmit={handleSearchSubmit} className="d-flex flex-wrap gap-2 mb-3">
                    <Form.Control
                      type="search"
                      placeholder="Search by referral code…"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      style={{ minWidth: "220px", maxWidth: "360px" }}
                    />
                    <Form.Select
                      className="w-auto"
                      value={statusFilter}
                      onChange={(e) => {
                        setStatusFilter(e.target.value);
                        setPage(1);
                      }}
                    >
                      <option value="">All status</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </Form.Select>
                    <Form.Select
                      className="w-auto"
                      value={affiliateFilter}
                      onChange={(e) => {
                        setAffiliateFilter(e.target.value);
                        setPage(1);
                      }}
                      disabled={affiliatesLoading}
                    >
                      <option value="">All affiliates</option>
                      {affiliateOptions.map((a) => {
                        const id = a._id || a.id;
                        return (
                          <option key={String(id)} value={String(id)}>
                            {a.name || a.email || id}
                          </option>
                        );
                      })}
                    </Form.Select>
                    <Button type="submit" variant="primary" disabled={loading}>
                      Search
                    </Button>
                  </Form>

                  <div className="admin-datatable-scroll">
                    <DataTable
                      columns={columns}
                      data={codes}
                      pagination
                      paginationServer
                      paginationTotalRows={totalRows}
                      paginationDefaultPage={page}
                      onChangePage={(newPage) => setPage(newPage)}
                      paginationPerPage={perPage}
                      paginationComponentOptions={dataTablePaginationOptions}
                      progressPending={loading}
                      progressComponent={<DataTableSkeleton rows={6} columns={8} />}
                      customStyles={dataTableCustomStyles}
                      striped
                      highlightOnHover
                      noDataComponent={
                        <AdminEmptyState
                          icon="ri-coupon-3-line"
                          title="No referral discount codes"
                          description="Create a code for an affiliate so customers can apply it at checkout."
                          actionLabel="Add Code"
                          onAction={() => handleShowFormModal()}
                        />
                      }
                    />
                  </div>
                </Card.Body>
              </Card>
            </Tab.Pane>

            <Tab.Pane eventKey="analytics">
              <ReferralDiscountAnalyticsPanel
                analytics={analytics}
                loading={analyticsLoading}
                error={analyticsError}
                onRefresh={fetchAnalytics}
              />
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>

        <Modal show={showFormModal} onHide={handleCloseFormModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>
              {isEdit ? "Edit Referral Discount Code" : "Create Referral Discount Code"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <AvForm onValidSubmit={handleValidSubmit}>
              <AvField
                name="affiliateId"
                label="Affiliate *"
                type="select"
                value={isEdit ? String(getAffiliateIdFromRow(editCode) || "") : ""}
                disabled={submitting || affiliatesLoading}
              >
                <option value="">
                  {affiliatesLoading ? "Loading affiliates…" : "Select affiliate"}
                </option>
                {affiliateOptions.map((a) => {
                  const id = a._id || a.id;
                  const label = `${a.name || "Affiliate"} — ${a.email || ""} (${a.affiliateReferralCode || a.referralCode || id})`;
                  return (
                    <option key={String(id)} value={String(id)}>
                      {label}
                    </option>
                  );
                })}
              </AvField>

              {!isEdit && (
                <AvField
                  name="referralCode"
                  label="Referral code *"
                  type="text"
                  placeholder="e.g. AFF10"
                  disabled={submitting}
                  style={{ textTransform: "uppercase" }}
                  validate={{
                    required: { value: true, errorMessage: "Referral code is required" },
                    pattern: {
                      value: /^[A-Za-z0-9_-]+$/,
                      errorMessage: "Use letters, numbers, hyphen or underscore only",
                    },
                  }}
                />
              )}

              {isEdit && (
                <Form.Group className="mb-3">
                  <Form.Label>Referral code</Form.Label>
                  <Form.Control
                    type="text"
                    value={editCode?.referralCode || ""}
                    readOnly
                    disabled
                  />
                  <Form.Text className="text-muted">
                    Code cannot be changed after creation.
                  </Form.Text>
                </Form.Group>
              )}

              <AvField
                name="discountPercentage"
                label="Discount % *"
                type="number"
                min="1"
                max="100"
                value={editCode?.discountPercentage ?? ""}
                disabled={submitting}
                validate={{
                  required: { value: true, errorMessage: "Discount percentage is required" },
                  min: { value: 1, errorMessage: "Minimum 1%" },
                  max: { value: 100, errorMessage: "Maximum 100%" },
                }}
              />

              <AvField
                name="maxUsage"
                label="Max usage (optional)"
                type="number"
                min="1"
                value={editCode?.maxUsage ?? ""}
                placeholder="Leave empty for unlimited"
                disabled={submitting}
              />

              {isEdit && (
                <Form.Group className="mb-3">
                  <Form.Label>Current usage</Form.Label>
                  <Form.Control
                    type="text"
                    value={String(editCode?.currentUsage ?? 0)}
                    readOnly
                    disabled
                  />
                </Form.Group>
              )}

              <AvField
                name="expiryDate"
                label="Expiry date (optional)"
                type="date"
                value={editExpiryValue}
                disabled={submitting}
              />

              <AvField
                name="status"
                label="Status"
                type="select"
                value={editCode?.status || "active"}
                disabled={submitting}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </AvField>

              <div className="d-flex gap-2 justify-content-end mt-3">
                <Button
                  variant="outline-secondary"
                  onClick={handleCloseFormModal}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={submitting}
                  style={{ backgroundColor: Utils.themeColor, border: "none" }}
                >
                  {submitting ? "Saving…" : isEdit ? "Update" : "Create"}
                </Button>
              </div>
            </AvForm>
          </Modal.Body>
        </Modal>

        <Modal show={Boolean(viewCode)} onHide={() => setViewCode(null)} centered size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Referral Discount Code Details</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <ReferralDiscountDetails
              code={viewCode}
              loading={viewLoading}
              affiliateName={viewCode ? resolveAffiliateName(viewCode) : ""}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setViewCode(null)}>
              Close
            </Button>
            {viewCode && (
              <Button
                variant="primary"
                style={{ backgroundColor: Utils.themeColor, border: "none" }}
                onClick={() => {
                  setViewCode(null);
                  handleShowFormModal(viewCode);
                }}
              >
                Edit
              </Button>
            )}
          </Modal.Footer>
        </Modal>

        <ConfirmModal
          show={confirm}
          onCloseClick={() => {
            if (!actionLoading) {
              setConfirm(false);
              setCurrentData(null);
            }
          }}
          onConfirm={handleConfirm}
          data={currentData}
        />

        <Footer />
      </div>
    </React.Fragment>
  );
}
